import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { driverAPI } from '../services/api';
import { showLoading, updateToSuccess, updateToError } from '../utils/toast';

const DriverLoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.license_number) {
      updateToError(null, '❌ Please enter both email and license number');
      return;
    }
    
    setIsSubmitting(true);
    const loadingToast = showLoading('Processing your request...');
    
    try {
      // First try to log in if it's an existing driver
      try {
        const loginResponse = await driverAPI.login(formData.email, formData.license_number);
        console.log('Driver login response:', loginResponse);
        
        if (loginResponse.data && (loginResponse.data.success || loginResponse.data.driver)) {
          const driverEmail = String(formData.email || '').trim();
          const driverData = loginResponse.data.driver || {};
          // Use the actual token from the API response if available, otherwise use a default
          const driverToken = loginResponse.data.token || loginResponse.headers['authorization'] || 'driver_authenticated';
          
          // Call the success callback with the driver's email and token
          if (typeof onLoginSuccess === 'function') {
            onLoginSuccess(driverEmail, driverToken, driverData);
          }
          
          updateToSuccess(loadingToast, '🎉 Welcome back! You are now logged in as a driver.');
          
          // Reset form and close modal after a short delay
          setFormData({
            name: '',
            email: '',
            phone: '',
            license_number: ''
          });
          
          setTimeout(() => {
            onClose();
            // Force a refresh to ensure all components update with the new driver status
            window.location.reload();
          }, 1500);
          
          return;
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (loginError) {
        console.log('Driver login error:', loginError);
        const errorData = loginError.response?.data;
        const status = loginError.response?.status;
        
        // Handle different login error scenarios
        if (status === 202 && errorData?.application_status === 'pending') {
          // Application is pending approval
          updateToSuccess(loadingToast, '⏳ Your driver application is pending admin approval. Please wait for approval.');
          setTimeout(() => onClose(), 3000);
          
        } else if (status === 403 && errorData?.application_status === 'rejected') {
          // Application was rejected
          const adminNotes = errorData.admin_notes || 'No additional notes provided';
          updateToError(loadingToast, `❌ Your driver application was rejected. Admin notes: ${adminNotes}`);
          
        } else if (status === 404 && errorData?.needs_application) {
          // No application found, need to submit new application
          console.log('No application found, attempting to submit new application...');
          
          // Check if we have all required fields for application
          if (!formData.name || !formData.phone) {
            updateToError(loadingToast, '❌ Please fill in all fields (Name, Email, Phone, License Number) to submit your driver application.');
            return;
          }
          
          try {
            const response = await driverAPI.applyAsDriver({
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              license_number: formData.license_number
            });
            
            if (response.data.status === 'pending') {
              updateToSuccess(loadingToast, '📋 Application submitted successfully! Admin will review your request.');
              setFormData({
                name: '',
                email: '',
                phone: '',
                license_number: ''
              });
              setTimeout(() => onClose(), 3000);
            } else if (response.data.status === 'approved') {
              updateToSuccess(loadingToast, '🎉 Driver found! You can now login as a driver.');
              setTimeout(() => onClose(), 2000);
            }
          } catch (applyError) {
            console.error('Driver application error:', applyError);
            const errorMsg = applyError.response?.data?.error || applyError.response?.data?.message || 'Failed to submit application';
            updateToError(loadingToast, `❌ ${errorMsg}`);
          }
          
        } else if (status === 401) {
          // Invalid credentials or inactive account
          updateToError(loadingToast, `❌ ${errorData?.message || 'Invalid credentials. Please check your email and license number.'}`);
          
        } else {
          // Other errors
          const errorMsg = errorData?.message || 'An error occurred during login';
          updateToError(loadingToast, `❌ ${errorMsg}`);
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      updateToError(loadingToast, '❌ An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center p-4 sm:p-6 backdrop-blur-md bg-black/60 overflow-y-auto" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="driver-login-title"
    >
      <div 
        className="relative w-full max-w-md mx-auto my-8 transform transition-all duration-500 scale-100 opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white hover:scale-110 transition-all duration-300 shadow-2xl group sm:-top-4 sm:-right-4 sm:p-3"
          aria-label="Close modal"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 group-hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="bg-white/95 backdrop-blur-md rounded-2xl sm:rounded-3xl border border-yellow-200 p-4 sm:p-6 md:p-8 shadow-2xl">
          <h2 id="driver-login-title" className="text-xl sm:text-2xl font-bold text-center mb-3 sm:mb-6 bg-gradient-to-r from-yellow-600 to-orange-500 bg-clip-text text-transparent">
            🚗 {t('driver.application.title')}
          </h2>
          <p className="text-center text-gray-600 mb-4 sm:mb-6 text-xs sm:text-sm">
            {t('driver.application.subtitle')}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <input
              type="text"
              name="name"
              placeholder={t('driver.application.full_name')}
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-400 bg-white/90"
              required
            />
            
            <input
              type="email"
              name="email"
              placeholder={t('driver.application.email_address')}
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-400 bg-white/90"
              required
            />
            
            <input
              type="tel"
              name="phone"
              placeholder={t('driver.application.phone_number')}
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-400 bg-white/90"
              required
            />
            
            <input
              type="text"
              name="license_number"
              placeholder={t('driver.application.license_number')}
              value={formData.license_number}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-400 bg-white/90"
              required
            />
            
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 font-bold rounded-lg transition-all duration-300 shadow-lg ${
                isSubmitting 
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-300 hover:to-yellow-400 transform hover:scale-105'
              }`}
            >
              {isSubmitting ? `⏳ ${t('driver.application.submitting')}` : `🚗 ${t('driver.application.submit_button')}`}
            </button>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              {t('driver.application.review_note')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DriverLoginModal;